// src/App.tsx
import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

import AppRoutes from './routes';
import { AuthProvider, useAuth } from './contexts/auth/provider';
import Header from './components/Header';
import BottomNav from './components/BottomNav';
import { useAccessGuard } from './components/appointments/DayView/hooks/useAccessGuard';
import ChoosePlanModal from './components/modals/ChoosePlanModal';
import PageScroll from './components/PageScroll';
import logo from './assets/mask-icon.svg';
import { supabase } from './lib/supabase';
import { getEffectiveBusinessId } from './lib/businessId';
import { UpdatePromptProvider, useUpdateStatus } from './components/UpdatePrompt';
import { Loader2 } from 'lucide-react';

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: false } },
});

const FORCE_HIDE_BOTTOM_NAV = false;

const pathStartsWithAny = (pathname: string, prefixes: string[]) =>
  prefixes.some((p) => pathname.startsWith(p));

function AppFrame() {
  const { isBlocked, loading: subscriptionLoading } = useAccessGuard();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasUpdate } = useUpdateStatus();

  // מה־AuthProvider: משתמש, ביזנס, סטטוס השלמה
  const { loading, hasAppUser, appUserCompleted, user, business } = useAuth();

  // Safety timeout to prevent infinite loading
  const [loadingTimedOut, setLoadingTimedOut] = React.useState(false);
  const [showTimeoutError, setShowTimeoutError] = React.useState(false);

  React.useEffect(() => {
    if (subscriptionLoading) {
      console.log('[App] Subscription loading started...');
      // Set a timeout of 10 seconds for subscription loading
      const timeout = setTimeout(() => {
        console.warn('[App] Subscription loading timeout after 10 seconds');
        setLoadingTimedOut(true);
        setShowTimeoutError(true);
      }, 10000);
      return () => clearTimeout(timeout);
    } else {
      // Reset timeout state when loading completes
      setLoadingTimedOut(false);
      setShowTimeoutError(false);
    }
  }, [subscriptionLoading]);

  const [forceHideByPopup, setForceHideByPopup] = useState(false);

  useEffect(() => {
    const handler = (ev: Event) => {
      const ce = ev as CustomEvent<{ hide?: boolean }>;
      if (typeof ce.detail?.hide === 'boolean') setForceHideByPopup(ce.detail.hide);
    };
    window.addEventListener('lf:bottomnav', handler as EventListener);
    return () => window.removeEventListener('lf:bottomnav', handler as EventListener);
  }, []);

  const hideHeaderOn = ['/', '/login', '/register', '/register/business', '/register/staff', '/book/','/update-password', '/forgot-password','/privacy'];
  const mustHideBottomNavOn = ['/', '/login', '/register', '/update-password', '/forgot-password', '/book/','/privacy'];
  const skipSubscriptionLoadingOn = ['/', '/book/', '/login', '/register'];

  const shouldHideHeader = pathStartsWithAny(location.pathname, hideHeaderOn);
  const shouldHideBottomNav = pathStartsWithAny(location.pathname, mustHideBottomNavOn);
  const shouldSkipSubscriptionLoading = pathStartsWithAny(location.pathname, skipSubscriptionLoadingOn);
  const hasBottomNav = !shouldHideBottomNav && !FORCE_HIDE_BOTTOM_NAV && !forceHideByPopup && !!user;

  useEffect(() => {
    document.documentElement.classList.toggle('has-bottom-nav', hasBottomNav);
  }, [hasBottomNav]);

  /**
   * ✅ כלל אוטומטי: אם יש מנוי (active/trialing/past_due) → users.completed = 1, אחרת = 0
   * רץ פעם אחת אחרי שיש לנו user.id (ואופציונלית business.id).
   * שינוי יחיד: לא מסתמכים על localStorage כדי להחליט אם לעדכן DB — תמיד מסנכרנים ל-DB ואז מעדכנים localStorage.
   */
  const didAutoCompleteRef = useRef(false);
  useEffect(() => {
    if (didAutoCompleteRef.current) return;
    if (!user?.id) return; // צריך משתמש
    // לא מריצים על דפי הרשמה כדי לא להתנגש בזרימה שלהם
    const isOnRegistration =
      location.pathname.startsWith('/register') ||
      location.pathname.startsWith('/registration');
    if (isOnRegistration) return;

    didAutoCompleteRef.current = true;

    (async () => {
      try {
       const businessId =
  business?.id ?? (user?.id ? await getEffectiveBusinessId(user.id) : null);


        // מצבים שמחשיבים כהשלמה: active, trialing, past_due
        const statuses = ['active', 'trialing', 'past_due'];

        let subRes:
          | { data: { id?: string; status?: string } | null; error: any }
          | undefined;

        if (businessId) {
          subRes = await supabase
            .from('subscriptions')
            .select('id,status')
            .eq('business_id', businessId)
            .in('status', statuses)
            .limit(1)
            .maybeSingle();
        } else {
          subRes = await supabase
            .from('subscriptions')
            .select('id,status')
            .eq('user_id', user.id)
            .in('status', statuses)
            .is('business_id', null) // אם אצלך יש גם מנויים בלי עסק
            .limit(1)
            .maybeSingle();
        }

        const hasSubscription = !!subRes?.data?.id;
        const nextCompleted = hasSubscription ? 1 : 0;

        // ✅ תמיד נסנכרן את ה־DB לערך המחושב
        const { error: updErr } = await supabase
          .from('users')
          .update({
            completed: nextCompleted,
            completed_at: hasSubscription ? new Date().toISOString() : null,
          })
          .eq('id', user.id);

        if (updErr) {
          // לא נכשיל את ה־UI אם RLS חוסם; רק נרשום ללוג

        }

        // ✅ ורק אחרי זה נעדכן localStorage לנוחות ה-UI
        localStorage.setItem('completed', String(nextCompleted));
      } catch (e) {

      }
    })();
  }, [user?.id, business?.id, location.pathname]);

  const hideHeaderStyle = shouldHideHeader ? { display: 'none' } : {};

  useEffect(() => {
    const fetchAndSetPlanOnAppLoad = async () => {
      if (!user?.id) return;

      try {
       const businessId =
  business?.id ?? (user?.id ? await getEffectiveBusinessId(user.id) : null);

        if (!businessId) {

          return;
        }

        const { data, error } = await supabase
          .from('subscriptions')
          .select('plan_id, stripe_subscription_id, stripe_customer_id')
          .eq('business_id', businessId)
          .maybeSingle();

        if (error) {

          return;
        }

        if (!data?.plan_id) {

          return;
        }

        const { data: planData, error: planError } = await supabase
          .from('subscription_plans')
          .select('code, plan_key')
          .eq('id', data.plan_id)
          .single();

        if (planError || !planData) {

          // ❌ DON'T overwrite localStorage on error
          return;
        }

        // ✅ ALWAYS update localStorage from Database (this is the source of truth!)
        const planCode = planData.code || planData.plan_key?.trim() || 'basic';
        localStorage.setItem('selectedPlan', planCode);

      } catch (err) {

        // ❌ DON'T overwrite localStorage on error
      }
    };

    fetchAndSetPlanOnAppLoad();
  }, [user?.id, business?.id]);

  // מסך טעינה ראשוני אם המנוי או המשתמש עדיין נטענים
  // Skip loading screen for external booking pages, login, and registration
  if ((subscriptionLoading && !loadingTimedOut) && !shouldSkipSubscriptionLoading) {
    return (
      <div dir="rtl" className="app-shell">
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-blue-50">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600 text-lg font-medium">טוען נתונים...</p>
          </div>
        </div>
      </div>
    );
  }

  // Timeout error screen
  // Skip timeout error screen for external pages, login, and registration
  if (showTimeoutError && !shouldSkipSubscriptionLoading) {
    return (
      <div dir="rtl" className="app-shell">
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-red-50 via-white to-red-50 p-4">
          <div className="text-center max-w-md">
            <div className="text-red-600 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">בעיה בטעינת הנתונים</h2>
            <p className="text-gray-600 mb-6">
              נראה שיש בעיה בחיבור לשרת או שיש מידע לא תקין בזיכרון.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => {
                  console.log('[App] Refreshing page after timeout');
                  window.location.reload();
                }}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                רענן את הדף
              </button>
              <button
                onClick={async () => {
                  console.log('[App] Clearing data and redirecting to login');
                  localStorage.clear();
                  await supabase.auth.signOut();
                  navigate('/login');
                }}
                className="w-full px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                התנתק ונסה שוב
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div dir="rtl" className="app-shell">
      {!hasUpdate && (
        <>
          {!shouldHideHeader && (
            <div className="header-fixed">
              <Header className="app-header-contents" />
            </div>
          )}
          {!shouldHideHeader && <div className="header-reserver" />}

          <main className="app-scroll">
            <PageScroll bottomPad={hasBottomNav} className={`${shouldHideHeader ? 'page-scroll--no-header ' : ''}space-y-6`}>
              <AppRoutes />
            </PageScroll>
          </main>

          {hasBottomNav && <BottomNav />}

          {!subscriptionLoading && isBlocked && <ChoosePlanModal />}
        </>
      )}

      <Toaster
        position="top-center"
        containerStyle={{
          top: 'calc(env(safe-area-inset-top) + 8px)',
          insetInline: 'max(8px, env(safe-area-inset-left)) max(8px, env(safe-area-inset-right))',
        }}
        gutter={8}
        toastOptions={{
          duration: 4000,
          style: { background: '#363636', color: '#fff', borderRadius: '10px' },
        }}
      />
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <UpdatePromptProvider>
            <AppFrame />
          </UpdatePromptProvider>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}
